import * as React from 'react';
import { Component, inject, deps, observer } from '../lib/component';
import { important, percent, px, url, viewWidth } from 'csx';
import { LoadingOverlay } from '../components/loading/overlay';
import { style } from 'typestyle';
import {
  center, centerCenter, horizontal, horizontallySpaced, startJustified, vertical,
  verticallySpaced
} from 'csstips';
import PrismicDOM from 'prismic-dom';
import {TextNoOrphan} from '../components/text-no-orphan';
import {
  colorContrast,
  colorSlate,
  colorWhite,
} from '../theme/color';
import {ButtonPill, buttonPillHelpers} from '../components/button-pill';
import {constantHeaderHeight} from '../theme/constant';
import {mediaTablet, mediaMobileOnly} from '../theme/media';
import {gradientHero} from '../theme/gradient';
import MainWrapper from '../components/main-wrapper';

export interface DiscoverPageProps {}

@inject(deps) @observer
export class DiscoverPage extends Component<DiscoverPageProps> {
  render() {
    const model = this.props.model.page.discover;

    return (
      <MainWrapper>
        <LoadingOverlay loading={model.loading} />
        {!model.loading && (
          <div className={DiscoverPage.styles.header} style={{ backgroundImage: this.getCoverImage() }}>
            <div>
              <h1>
                <TextNoOrphan value={model.page_title} />
              </h1>
              {model.page_subtitle && (
                <p>
                  <TextNoOrphan value={model.page_subtitle} />
                </p>
              )}
              <div className={DiscoverPage.styles.cta} dangerouslySetInnerHTML={{ __html: decodeURIComponent(model.hubspot_cta) }} />
            </div>
            <div className={DiscoverPage.styles.video}>
              <div dangerouslySetInnerHTML={{ __html: decodeURIComponent(model.video) }} />
            </div>
          </div>
        )}
        {!model.loading && (
          <div className={DiscoverPage.styles.content}>
            <div className={DiscoverPage.styles.body} dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(model.page_body) }} />
            <div className={DiscoverPage.styles.cta} dangerouslySetInnerHTML={{ __html: decodeURIComponent(model.hubspot_cta) }} />
          </div>
        )}
      </MainWrapper>
    )
  }

  getCoverImage = () => {
    const { cover_image } = this.props.model.page.discover;
    if (!cover_image) return;

    return `${gradientHero}, ${url(cover_image)}`;
  };

  static styles = {
    header: style(
      {
        backgroundColor: colorSlate.toString(),
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        $nest: {
          '& h1': {
            color: colorWhite.toString(),
            width: percent(100),
            margin: 0,
            fontSize: viewWidth(12),
            wordWrap: 'break-word'
          },
          '& p': {
            color: colorWhite.toString(),
            width: percent(100),
            maxWidth: px(400),
            fontSize: px(22),
            fontWeight: 200,
            margin: '20px 0 60px'
          }
        }
      },
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(40),
        minHeight: px(430),
        padding: `calc(20px + ${constantHeaderHeight}) 20px 20px`,
        $nest: {
          '&> div': {
            width: percent(100),
          },
          '& p': {
            color: colorWhite.toString(),
            width: percent(100),
            maxWidth: px(400),
            fontSize: px(22),
            fontWeight: 200,
            margin: '20px 0 40px'
          }
        }
      }),
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(100),
        ...center,
        ...startJustified,
        height: 'calc(100vh - 160px)',
        minHeight: px(640),
        padding: `calc(100px + ${constantHeaderHeight}) 100px 100px`,
        $nest: {
          '&> div': {
            flex: 1,
          },
          '& h1': {
            fontSize: viewWidth(4.5)
          }
        }
      })
    ),
    content: style(
      {
        ...vertical,
        ...center,
        ...startJustified,
      },
      mediaMobileOnly({ padding: px(20), ...verticallySpaced(40)}),
      mediaTablet({ padding: px(100), ...verticallySpaced(100), $nest: { '& p': { fontSize: px(20) }} })
    ),
    body: style({
      maxWidth: px(960),
      $nest: {
        '& > *:first-child': {
          marginTop: 0
        },
        '& > *:last-child': {
          marginBottom: 0
        }
      }
    }),
    video: style(
      {
        ...centerCenter,
        flex: 1,
        margin: '0 auto',
        position: 'relative',
        $nest: {
          '& div': {
            position: 'relative',
            width: percent(100),
            paddingTop: percent(100 * 320/640),
          },
          '& iframe': {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: percent(100),
            height: percent(100)
          }
        }
      },
      mediaMobileOnly({
        $nest: {
          '& div': {
            paddingTop: percent(100 * 480/640)
          }
        }
      })
    ),
    cta: style(
      { $nest: { '& a': { fontSize: important('20px') }}},
      { $nest: { '& a': buttonPillHelpers.customColor(colorContrast) }},
      { $nest: { '& a': buttonPillHelpers.customColorLink() }},
      { $nest: { '& a': buttonPillHelpers.bouncing }},
      mediaMobileOnly({ $nest: { '& a': { fontSize: important('20px') }}}),
      mediaTablet({ $nest: { '& a': { fontSize: important('24px') }}})
    )
  }
}